import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const BASE = "/api/analytics/reconciliation";

/** Board: system cases (left), bank lines (right), matches, statements. */
export const useReconciliation = (year, month) =>
  useQuery({
    queryKey: ["reconciliation", year, month],
    queryFn: async () => {
      const { data } = await axios.get(BASE, { params: { year, month } });
      return data; // { cases, lines, matches, statements, year, month }
    },
    staleTime: 60 * 1000,
    keepPreviousData: true,
  });

export const uploadStatement = async (file, year, month) => {
  const form = new FormData();
  form.append("file", file);
  form.append("year", year);
  if (month) form.append("month", month);
  const { data } = await axios.post(`${BASE}/statements`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const deleteStatement = async (id) => {
  const { data } = await axios.delete(`${BASE}/statements/${id}`);
  return data;
};

/** Re-parse an uploaded statement with the current parser (recovers full descriptions). */
export const reparseStatement = async (id) => {
  const { data } = await axios.post(`${BASE}/statements/${id}/reparse`);
  return data;
};

/** hiddenLineIds: lines the user has hidden on the board. Sent so the run skips
 *  them — hiding is a per-browser preference, so the server can't know it. */
export const autoMap = async (year, month, hiddenLineIds = []) => {
  const { data } = await axios.post(`${BASE}/auto-map`, { hidden_line_ids: hiddenLineIds }, { params: { year, month } });
  return data; // { matches }
};

export const reconcileCost = async (year, month, hiddenLineIds = []) => {
  const { data } = await axios.post(`${BASE}/reconcile-cost`, { hidden_line_ids: hiddenLineIds }, { params: { year, month } });
  return data; // { matches } — debit lines matched to order internal costs
};

export const createMatch = async (orderId, lineId) => {
  const { data } = await axios.post(`${BASE}/matches`, { order_id: orderId, line_id: lineId });
  return data; // { match }
};

export const updateMatch = async (id, action) => {
  const { data } = await axios.patch(`${BASE}/matches/${id}`, { action }); // confirm|reject
  return data;
};

export const deleteMatch = async (id) => {
  const { data } = await axios.delete(`${BASE}/matches/${id}`);
  return data;
};

/** Split one bank line across multiple orders (Xero-style). allocations:
 *  [{ order_id, amount }] where amount is in PENCE (GBP). Replaces the line's
 *  current allocations wholesale (orders omitted are unlinked). Partial allowed;
 *  over-allocation (sum > line total) is rejected server-side (422). */
export const splitLine = async (lineId, allocations) => {
  const { data } = await axios.post(`${BASE}/lines/${lineId}/split`, { allocations });
  return data; // { matches, allocated, remaining }
};

/** Correct a bank line's side (debit ⇄ credit) — e.g. an inbound payment the
 *  AI parser put in the wrong column. Persists + re-syncs any matched order. */
export const updateLineDirection = async (id, direction) => {
  const { data } = await axios.patch(`${BASE}/lines/${id}`, { direction }); // debit|credit
  return data;
};

/** Search paid orders across ALL months (for the split editor, so one line can
 *  be allocated to orders in different periods). Returns [{order_id, ...}]. */
export const searchOrders = async (q) => {
  const { data } = await axios.get(`${BASE}/orders/search`, { params: { q } });
  return data.orders || [];
};

/** Resolve specific orders by id (to label allocations already on a line whose
 *  order isn't on the current month's board). */
export const resolveOrders = async (ids) => {
  if (!ids || !ids.length) return [];
  const { data } = await axios.get(`${BASE}/orders/search`, { params: { ids: ids.join(",") } });
  return data.orders || [];
};

/** Move a bank line onto a different month's board (per-line period override).
 *  Pass {year, month} to move; pass {year:null} to reset to its statement period.
 *  The line keeps showing its origin month as a "⤺ from <month>" badge. */
export const moveLine = async (id, year, month) => {
  const { data } = await axios.patch(`${BASE}/lines/${id}/move`, { year, month });
  return data;
};

/** Void a duplicate order: voids it in Xero, then cancels + strips it locally. */
export const voidOrder = async (orderId) => {
  const { data } = await axios.post(`${BASE}/orders/${orderId}/void`);
  return data; // { ok, report } or { ok:false, error }
};

/** Upload a PO/invoice against a listing without leaving the board. Lands in the
 *  listing's "Upload Invoice" tab (listing_files pivot, type='invoice'). */
export const uploadListingDocument = async (listingId, file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await axios.post(`${BASE}/listings/${listingId}/documents`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data; // { document: { id, name, url, pdf_url } }
};

/** An order's Xero invoice PDF, as a blob: URL ready for <object data=…>.
 *  Fetched through axios so the auth header goes with it — an <object> makes an
 *  anonymous sub-resource request, so pointing it at the route directly 403s.
 *  (PO and uploaded documents don't need this: they arrive pre-signed.)
 *  Caller owns the URL and should URL.revokeObjectURL() it when done. */
export const fetchOrderInvoicePdf = async (orderId) => {
  const { data } = await axios.get(`${BASE}/orders/${orderId}/invoice-pdf`, { responseType: "blob" });
  return URL.createObjectURL(data);
};

/** Merchant-taught AI hints: library shown in the UI and fed to the Gemini prompts. */
export const useReconciliationHints = () =>
  useQuery({
    queryKey: ["reconciliation-hints"],
    queryFn: async () => {
      const { data } = await axios.get(`${BASE}/hints`);
      return data; // { hints }
    },
    staleTime: 60 * 1000,
  });

export const createHint = async (payload) => {
  // payload: { scope: purchase|cost|revenue|all, label?, statement_hint?, system_hint?, note? }
  const { data } = await axios.post(`${BASE}/hints`, payload);
  return data; // { hint }
};

export const deleteHint = async (id) => {
  const { data } = await axios.delete(`${BASE}/hints/${id}`);
  return data;
};
